import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Grid,
    MenuItem,
    Chip,
    CircularProgress,
    Stack,
    IconButton,
    Tooltip,
    Avatar,
    LinearProgress,
    InputAdornment,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    alpha
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Print as PrintIcon,
    FileDownload as FileDownloadIcon,
    Visibility as VisibilityIcon,
    Receipt as ReceiptIcon,
    FolderShared as FolderIcon,
    AccountBalanceWallet as WalletIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorIcon,
    FilterAlt as FilterIcon,
    TrendingUp as TrendingUpIcon,
    Description as DescriptionIcon,
    Payment as PaymentIcon,
    MenuBook as MenuBookIcon,
    SwapHoriz as SwapHorizIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFactures } from '../../data/slices/factureSlice';
import { fetchClients } from '../../data/slices/clientStore';
import { fetchCircuits } from '../../data/slices/circuitSlice';
import { COLORS, jsonToXlsx } from '../../settings/themeSettings';
import PageHeader from '../../components/layout/PageHeader';
import { formatCurrency, formatDate, parseAnyDate } from '../../utils/compteClientEngine';
import facturationService from '../../services/facturationService';
import { notify } from '../../settings/toastSetting';

const PRESTATIONS = [
    { value: 'ALL', label: 'Toutes les prestations' },
    { value: 'BILLETTERIE', label: 'Billetterie' },
    { value: 'CIRCUIT', label: 'Circuit' },
    { value: 'SEJOUR', label: 'Séjour' },
    { value: 'EXCURSION', label: 'Excursion' },
    { value: 'TRANSFERT', label: 'Transfert' }
];

const STATUTS = [
    { value: 'ALL', label: 'Tous les statuts' },
    { value: 'PAYEE', label: 'Payée' },
    { value: 'IMPAYEE', label: 'Impayée' },
    { value: 'PARTIEL', label: 'Partielle' },
    { value: 'ANNULLEE', label: 'Annulée' }
];

const GestionClientPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { factures, fetchStatus } = useSelector((state) => state.factures);

    // Filtres de recherche
    const [numDossierQuery, setNumDossierQuery] = useState('');
    const [clientQuery, setClientQuery] = useState('');
    const [statutFilter, setStatutFilter] = useState('ALL');
    const [prestationFilter, setPrestationFilter] = useState('ALL');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    // Sélection active au clavier
    const [activeRowIdx, setActiveRowIdx] = useState(0);

    useEffect(() => {
        dispatch(fetchFactures());
        dispatch(fetchClients());
        dispatch(fetchCircuits());
    }, [dispatch]);

    // Filtrage dynamique des factures
    const filteredFactures = useMemo(() => {
        if (!Array.isArray(factures)) return [];

        return factures.filter(fact => {
            // 1. N° Dossier
            if (numDossierQuery) {
                const dossierRef = fact.numeroDossier || fact.dossier?.reference || fact.dossier || '';
                if (!String(dossierRef).toLowerCase().includes(numDossierQuery.toLowerCase())) {
                    return false;
                }
            }

            // 2. Nom Client
            if (clientQuery) {
                const c = fact.toClient || fact.client;
                const cName = c?.libelle || c?.nomComplet || c?.nom || fact.nomClient || (typeof c === 'string' ? c : '');
                if (!cName.toLowerCase().includes(clientQuery.toLowerCase())) {
                    return false;
                }
            }

            // 3. Statut
            if (statutFilter !== 'ALL') {
                const s = (fact.statut || fact.status || 'IMPAYEE').toUpperCase();
                if (statutFilter === 'PAYEE' && s !== 'PAYEE' && s !== 'PAYÉ') return false;
                if (statutFilter === 'IMPAYEE' && s !== 'IMPAYEE' && s !== 'IMPAYÉ' && s !== 'NON_PAYE') return false;
                if (statutFilter === 'PARTIEL' && s !== 'PARTIEL' && s !== 'PARTIELLEMENT_PAYEE') return false;
                if (statutFilter === 'ANNULLEE' && s !== 'ANNULLEE' && s !== 'ANNULÉ') return false;
            }

            // 4. Type de prestation
            if (prestationFilter !== 'ALL') {
                const typeF = (fact.typeFacture || fact.type || fact.nature || '').toUpperCase();
                const dType = (fact.dossier?.type || '').toUpperCase();
                if (prestationFilter === 'BILLETTERIE' && !(typeF === 'FB' || typeF.includes('BILLET') || fact.tickets?.length > 0)) return false;
                if (prestationFilter === 'CIRCUIT' && !(typeF === 'FC' || dType.includes('CIRCUIT') || typeF.includes('CIRCUIT'))) return false;
                if (prestationFilter === 'SEJOUR' && !(dType.includes('SEJOUR') || dType.includes('SÉJOUR') || typeF.includes('SEJOUR'))) return false;
                if (prestationFilter === 'EXCURSION' && !(dType.includes('EXCURSION') || typeF.includes('EXCURSION'))) return false;
                if (prestationFilter === 'TRANSFERT' && !(dType.includes('TRANSFERT') || typeF.includes('TRANSFERT'))) return false;
            }

            // 5. Période
            if (dateDebut || dateFin) {
                const factDate = parseAnyDate(fact.date || fact.createdAt);
                if (factDate && factDate.isValid()) {
                    if (dateDebut && factDate.isBefore(parseAnyDate(dateDebut).startOf('day'))) return false;
                    if (dateFin && factDate.isAfter(parseAnyDate(dateFin).endOf('day'))) return false;
                }
            }

            return true;
        });
    }, [factures, numDossierQuery, clientQuery, statutFilter, prestationFilter, dateDebut, dateFin]);

    // Calcul des totaux dynamiques (au-dessus du tableau)
    const totals = useMemo(() => {
        let totalMontant = 0;
        let totalPaye = 0;
        let totalSolde = 0;

        filteredFactures.forEach(fact => {
            const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
            let paye = 0;
            if (Array.isArray(fact.paiements)) {
                paye = fact.paiements.reduce((sum, p) => sum + Number(p.montant || 0), 0);
            } else if (fact.dejaPaye) {
                paye = Number(fact.dejaPaye);
            }
            const solde = fact.solde !== undefined ? Number(fact.solde) : (montant - paye);

            totalMontant += montant;
            totalPaye += paye;
            totalSolde += solde;
        });

        const tauxRecouvrement = totalMontant > 0 ? Math.min(100, Math.round((totalPaye / totalMontant) * 100)) : 100;

        return { totalMontant, totalPaye, totalSolde, tauxRecouvrement };
    }, [filteredFactures]);

    const handleRowClick = React.useCallback((fact) => {
        if (!fact) return;
        const factId = fact.id;
        if (!factId) return;

        // Si la facture est issue d'un dossier (circuit, séjour, etc.)
        if (fact.dossier) {
            const dossierId = typeof fact.dossier === 'object' ? fact.dossier.id : fact.dossier;
            const type = (fact.dossier && typeof fact.dossier === 'object' ? fact.dossier.type : null) || fact.typeDossier || 'circuit';
            navigate(`/dossiers/${type}/${dossierId}/factures/${factId}`);
            return;
        }

        // Sinon facture billetterie / client
        const clientId = typeof fact.client === 'object' 
            ? fact.client?.id 
            : (fact.toClient?.id || fact.client || 'COMPTANT');
        
        navigate(`/billeterie/factures/${clientId}/details/${factId}`);
    }, [navigate]);

    const handlePrintClick = (fact) => {
        if (!fact || !fact.id) return;
        const clientId = typeof fact.client === 'object' 
            ? fact.client?.id 
            : (fact.toClient?.id || fact.client || 'COMPTANT');
        navigate(`/billeterie/facture/${clientId}/${fact.id}/pdf`);
    };

    // Navigation globale au clavier dans la liste (flèches haut/bas et entrée)
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Ignorer si l'utilisateur saisit dans un champ de formulaire
            const activeEl = document.activeElement;
            const isInputFocused = activeEl && (
                activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.tagName === 'SELECT' ||
                activeEl.isContentEditable
            );
            if (isInputFocused) return;

            if (!filteredFactures || filteredFactures.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveRowIdx(prev => Math.min(filteredFactures.length - 1, prev + 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveRowIdx(prev => Math.max(0, prev - 1));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selected = filteredFactures[activeRowIdx];
                if (selected) {
                    handleRowClick(selected);
                }
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [filteredFactures, activeRowIdx, handleRowClick]);

    // Défilement automatique pour garder la ligne active visible
    useEffect(() => {
        const rowEl = document.getElementById(`gestion-facture-row-${activeRowIdx}`);
        if (rowEl) {
            rowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [activeRowIdx]);

    const handleReset = () => {
        setNumDossierQuery('');
        setClientQuery('');
        setStatutFilter('ALL');
        setPrestationFilter('ALL');
        setDateDebut('');
        setDateFin('');
        setActiveRowIdx(0);
    };

    const handleExportExcel = () => {
        const dataToExport = filteredFactures.map(f => {
            const c = f.toClient || f.client;
            const cName = c?.libelle || c?.nomComplet || c?.nom || f.nomClient || '-';
            const montant = Number(f.montant || f.total || f.totalFacture || 0);
            let paye = 0;
            if (Array.isArray(f.paiements)) paye = f.paiements.reduce((s, p) => s + Number(p.montant || 0), 0);
            else if (f.dejaPaye) paye = Number(f.dejaPaye);
            const solde = montant - paye;

            return {
                'N° Dossier': f.numeroDossier || f.dossier?.reference || '-',
                'Client': cName,
                'N° Facture': f.numeroFacture || `F-${f.id}`,
                'Date': formatDate(f.date || f.createdAt),
                'Statut': f.statut || 'IMPAYEE',
                'Montant (CFA)': montant,
                'Déjà payé (CFA)': paye,
                'Solde dû (CFA)': solde
            };
        });
        jsonToXlsx(dataToExport, "Gestion_Clients_Factures");
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDeleteFacture = async (fact) => {
        const numFact = fact.numeroFacture || `F-${fact.id}`;
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement la facture ${numFact} ? Cette action est irréversible.`)) {
            try {
                await facturationService.deleteFacture(fact.id);
                notify(`Facture ${numFact} supprimée avec succès`, "success");
                dispatch(fetchFactures());
            } catch (err) {
                console.error("Erreur suppression facture:", err);
                notify("Erreur lors de la suppression de la facture", "error");
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Page Header */}
            <PageHeader
                title="Gestion client"
                subtitle="Recherche, consultation des dossiers / factures et création"
                icon={FolderIcon}
                breadcrumbs={[
                    { label: 'Facturation', path: '/facturation/gestion-client' },
                    { label: 'Gestion client', path: '/facturation/gestion-client' }
                ]}
                actions={[
                    {
                        label: 'Nouvelle facture',
                        icon: <AddIcon />,
                        onClick: () => navigate('/facturation/nouvelle'),
                        variant: 'contained',
                        color: 'primary'
                    },
                    {
                        label: 'Saisie encaissement',
                        icon: <ReceiptIcon />,
                        onClick: () => navigate('/facturation/encaissements'),
                        variant: 'outlined',
                        color: 'secondary'
                    },
                    {
                        label: 'Compte client',
                        icon: <FolderIcon />,
                        onClick: () => navigate('/facturation/compte-client'),
                        variant: 'outlined',
                        color: 'primary'
                    }
                ]}
            />

            {/* KPI STATS CARDS */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* Total Facturé */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Total facturé
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                                {formatCurrency(totals.totalMontant)} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                {filteredFactures.length} facture{filteredFactures.length > 1 ? 's' : ''} affichée{filteredFactures.length > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, width: 48, height: 48 }}>
                            <WalletIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* Total Encaissé */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Total encaissé
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.5 }}>
                                {formatCurrency(totals.totalPaye)} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                                <CheckCircleIcon sx={{ fontSize: 13 }} /> Règlements validés
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: '#dcfce7', color: '#16a34a', width: 48, height: 48 }}>
                            <TrendingUpIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* Reste à recouvrer / Impayés */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: totals.totalSolde > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                            bgcolor: totals.totalSolde > 0 ? '#fff5f5' : 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: totals.totalSolde > 0 ? '#b91c1c' : '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Reste à recouvrer
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: totals.totalSolde > 0 ? '#dc2626' : '#16a34a', mt: 0.5 }}>
                                {formatCurrency(totals.totalSolde)} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: totals.totalSolde > 0 ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                                {totals.totalSolde > 0 ? <ErrorIcon sx={{ fontSize: 13 }} /> : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                                {totals.totalSolde > 0 ? 'Soldes en attente' : 'Entièrement soldé'}
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: totals.totalSolde > 0 ? '#fee2e2' : '#f1f5f9', color: totals.totalSolde > 0 ? '#dc2626' : '#64748b', width: 48, height: 48 }}>
                            <ErrorIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* Taux de recouvrement */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Recouvrement
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: totals.tauxRecouvrement >= 80 ? '#16a34a' : '#ea580c' }}>
                                {totals.tauxRecouvrement}%
                            </Typography>
                        </Stack>
                        <Box sx={{ my: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={totals.tauxRecouvrement}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        bgcolor: totals.tauxRecouvrement >= 80 ? '#16a34a' : (totals.tauxRecouvrement >= 50 ? '#f59e0b' : '#dc2626')
                                    }
                                }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Ratio encaissé / facturé
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* FORMULAIRE DE RECHERCHE AVEC FILTRES RAPIDES */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, md: 3 },
                    mb: 3,
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    bgcolor: 'white',
                    boxShadow: '0 4px 20px -2px rgba(0,0,0,0.04)'
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2.5 }} spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary }}>
                            <FilterIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Critères de recherche & Filtres
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Affinez la liste par dossier, client, statut ou période
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Filtres rapides de statut (Pill buttons) */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {STATUTS.map(s => {
                            const active = statutFilter === s.value;
                            return (
                                <Chip
                                    key={s.value}
                                    label={s.label}
                                    size="small"
                                    onClick={() => setStatutFilter(s.value)}
                                    sx={{
                                        fontWeight: active ? 700 : 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        bgcolor: active ? '#0f172a' : '#f1f5f9',
                                        color: active ? 'white' : '#475569',
                                        '&:hover': {
                                            bgcolor: active ? '#1e293b' : '#e2e8f0'
                                        }
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Stack>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="N° dossier"
                            placeholder="Recherche directe..."
                            value={numDossierQuery}
                            onChange={(e) => setNumDossierQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DescriptionIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3.5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nom client"
                            placeholder="Recherche dynamique..."
                            value={clientQuery}
                            onChange={(e) => setClientQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Type de prestation"
                            value={prestationFilter}
                            onChange={(e) => setPrestationFilter(e.target.value)}
                        >
                            {PRESTATIONS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Date début"
                            InputLabelProps={{ shrink: true }}
                            value={dateDebut}
                            onChange={(e) => setDateDebut(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            label="Date fin"
                            InputLabelProps={{ shrink: true }}
                            value={dateFin}
                            onChange={(e) => setDateFin(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleReset}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            color: '#475569',
                            borderColor: '#cbd5e1',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
                        }}
                    >
                        Réinitialiser
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={() => {}}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            px: 3,
                            bgcolor: '#0f172a',
                            '&:hover': { bgcolor: '#1e293b' }
                        }}
                    >
                        Filtrer
                    </Button>
                </Stack>
            </Paper>

            {/* Actions Bar & Export */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                        Résultats de recherche
                    </Typography>
                    <Chip
                        label={`${filteredFactures.length} facture${filteredFactures.length > 1 ? 's' : ''}`}
                        size="small"
                        sx={{ bgcolor: '#e2e8f0', color: '#334155', fontWeight: 700, fontSize: '0.75rem' }}
                    />
                </Stack>
                <Stack direction="row" spacing={1.2}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            color: '#334155',
                            borderColor: '#cbd5e1',
                            bgcolor: 'white'
                        }}
                    >
                        Imprimer la liste
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            px: 2,
                            boxShadow: 'none',
                            bgcolor: '#16a34a',
                            '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }
                        }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* TABLEAU DES RÉSULTATS AVEC COLONNES DÉLIMITÉES STRICTEMENT ET DÉFILEMENT HORIZONTAL FLUIDE */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    borderRadius: '16px',
                    border: '1px solid #cbd5e1',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: '65vh',
                    bgcolor: 'white',
                    boxShadow: '0 10px 30px -4px rgba(0,0,0,0.05)',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#94a3b8 #f1f5f9',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '10px'
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#94a3b8',
                        borderRadius: '6px',
                        border: '2px solid #f1f5f9',
                        '&:hover': {
                            bgcolor: '#64748b'
                        }
                    }
                }}
            >
                <Table stickyHeader sx={{ minWidth: 1320, tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '130px' }} />
                        <col style={{ width: '280px' }} />
                        <col style={{ width: '160px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '145px' }} />
                        <col style={{ width: '145px' }} />
                        <col style={{ width: '220px' }} />
                    </colgroup>

                    <TableHead>
                        {/* 1. LIGNE DES TOTAUX AU-DESSUS DE LA LIGNE DES TITRES (SECTION 3.3) */}
                        <TableRow sx={{ bgcolor: '#f8fafc', borderBottom: '2px solid #0f172a' }}>
                            <TableCell
                                colSpan={5}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRight: '1px solid #cbd5e1',
                                    borderBottom: '2px solid #0f172a',
                                    bgcolor: '#f8fafc',
                                    textAlign: 'right'
                                }}
                            >
                                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                    <WalletIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: '#334155' }}>
                                        Totaux des résultats affichés :
                                    </Typography>
                                </Stack>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRight: '1px solid #cbd5e1',
                                    borderBottom: '2px solid #0f172a',
                                    bgcolor: '#f8fafc',
                                    fontWeight: 900,
                                    fontSize: '0.95rem',
                                    color: '#0f172a',
                                    fontVariantNumeric: 'tabular-nums'
                                }}
                            >
                                {formatCurrency(totals.totalMontant)} <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRight: '1px solid #cbd5e1',
                                    borderBottom: '2px solid #0f172a',
                                    bgcolor: '#f8fafc',
                                    fontWeight: 900,
                                    fontSize: '0.95rem',
                                    color: totals.totalSolde > 0 ? '#dc2626' : '#16a34a',
                                    fontVariantNumeric: 'tabular-nums'
                                }}
                            >
                                {formatCurrency(totals.totalSolde)} <span style={{ fontSize: '0.72rem', fontWeight: 600, color: totals.totalSolde > 0 ? '#ef4444' : '#64748b' }}>CFA</span>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, px: 1, borderBottom: '2px solid #0f172a', bgcolor: '#f8fafc' }} />
                        </TableRow>

                        {/* 2. LIGNE DES TITRES DE COLONNES (FIGÉE / STICKY AVEC DÉLIMITATIONS STRICTES) */}
                        <TableRow>
                            <TableCell sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                N° Dossier
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                Client
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                N° Facture
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                Date
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                Statut
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                Montant
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                Solde dû
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#0f172a', color: 'white', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, px: 1 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {fetchStatus === 'loading' ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                    <CircularProgress size={36} sx={{ color: COLORS.primary, mb: 2 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Chargement des dossiers et factures en cours...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredFactures.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                    <Avatar sx={{ width: 56, height: 56, bgcolor: '#f1f5f9', color: '#94a3b8', mx: 'auto', mb: 2 }}>
                                        <SearchIcon sx={{ fontSize: 30 }} />
                                    </Avatar>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', mb: 0.5 }}>
                                        Aucun résultat trouvé
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                                        Aucun dossier ou facture ne correspond à vos critères de recherche.
                                    </Typography>
                                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
                                        Réinitialiser les filtres
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredFactures.map((fact, idx) => {
                                const isSelected = activeRowIdx === idx;
                                const c = fact.toClient || fact.client;
                                const clientName = c?.libelle || c?.nomComplet || c?.nom || fact.nomClient || '-';
                                const dossierRef = fact.numeroDossier || fact.dossier?.reference || '-';
                                const numFact = fact.numeroFacture || `F-${fact.id}`;
                                const dateStr = formatDate(fact.date || fact.createdAt);
                                const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
                                
                                let paye = 0;
                                if (Array.isArray(fact.paiements)) paye = fact.paiements.reduce((s, p) => s + Number(p.montant || 0), 0);
                                else if (fact.dejaPaye) paye = Number(fact.dejaPaye);
                                const solde = fact.solde !== undefined ? Number(fact.solde) : (montant - paye);

                                const isPayee = solde <= 0.01;
                                const isPartiel = solde > 0.01 && paye > 0;

                                const clientInitials = clientName !== '-'
                                    ? clientName.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase()
                                    : 'CL';

                                return (
                                    <TableRow
                                        id={`gestion-facture-row-${idx}`}
                                        key={fact.id || idx}
                                        hover
                                        onClick={() => {
                                            setActiveRowIdx(idx);
                                            handleRowClick(fact);
                                        }}
                                        onMouseEnter={() => setActiveRowIdx(idx)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: isSelected 
                                                ? 'rgba(37, 99, 235, 0.09) !important' 
                                                : (idx % 2 === 0 ? 'white' : '#fcfdfe'),
                                            borderLeft: isSelected 
                                                ? `4px solid ${COLORS.primary || '#2563eb'}` 
                                                : '4px solid transparent',
                                            transition: 'all 0.15s ease-in-out',
                                            '&:hover': {
                                                bgcolor: isSelected 
                                                    ? 'rgba(37, 99, 235, 0.14) !important' 
                                                    : 'rgba(241, 245, 249, 0.9) !important'
                                            }
                                        }}
                                    >
                                        {/* N° Dossier */}
                                        <TableCell sx={{ py: 1.3, px: 2, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0' }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#334155',
                                                    bgcolor: '#f1f5f9',
                                                    px: 1,
                                                    py: 0.4,
                                                    borderRadius: '6px',
                                                    display: 'inline-block',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.8rem',
                                                    border: '1px solid #cbd5e1'
                                                }}
                                            >
                                                {dossierRef}
                                            </Typography>
                                        </TableCell>

                                        {/* Client */}
                                        <TableCell sx={{ py: 1.3, px: 2, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0' }}>
                                            <Tooltip title={clientName}>
                                                <Stack direction="row" alignItems="center" spacing={1.2} sx={{ overflow: 'hidden' }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: '0.72rem',
                                                            fontWeight: 800,
                                                            flexShrink: 0,
                                                            bgcolor: isSelected ? COLORS.primary : '#e2e8f0',
                                                            color: isSelected ? 'white' : '#475569'
                                                        }}
                                                    >
                                                        {clientInitials}
                                                    </Avatar>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#0f172a',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {clientName}
                                                    </Typography>
                                                </Stack>
                                            </Tooltip>
                                        </TableCell>

                                        {/* N° Facture */}
                                        <TableCell sx={{ py: 1.3, px: 2, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: COLORS.primary || '#2563eb',
                                                    fontFamily: 'monospace',
                                                    letterSpacing: 0.2,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {numFact}
                                            </Typography>
                                        </TableCell>

                                        {/* Date */}
                                        <TableCell sx={{ py: 1.3, px: 2, color: '#64748b', fontSize: '0.82rem', fontWeight: 500, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                            {dateStr}
                                        </TableCell>

                                        {/* Statut Badge */}
                                        <TableCell sx={{ py: 1.3, px: 2, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0' }}>
                                            <Chip
                                                label={isPayee ? 'Payée' : (isPartiel ? 'Partielle' : 'Impayée')}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.72rem',
                                                    fontWeight: 800,
                                                    height: '24px',
                                                    borderRadius: '6px',
                                                    bgcolor: isPayee ? '#dcfce7' : (isPartiel ? '#fef3c7' : '#fee2e2'),
                                                    color: isPayee ? '#15803d' : (isPartiel ? '#b45309' : '#b91c1c'),
                                                    border: `1px solid ${isPayee ? '#bbf7d0' : (isPartiel ? '#fde68a' : '#fecaca')}`
                                                }}
                                            />
                                        </TableCell>

                                        {/* Montant */}
                                        <TableCell align="right" sx={{ py: 1.3, px: 2, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                            {formatCurrency(montant)}
                                        </TableCell>

                                        {/* Solde dû */}
                                        <TableCell align="right" sx={{ py: 1.3, px: 2, fontWeight: 800, color: solde > 0.01 ? '#dc2626' : '#16a34a', fontVariantNumeric: 'tabular-nums', borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                            {formatCurrency(solde)}
                                        </TableCell>

                                        {/* Actions complètes */}
                                        <TableCell align="center" sx={{ py: 1.3, px: 1, borderBottom: '1px solid #e2e8f0' }}>
                                            <Stack direction="row" spacing={0.6} justifyContent="center" alignItems="center">
                                                <Tooltip title="Consulter le détail (Entrée)">
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            color: COLORS.primary || '#2563eb',
                                                            bgcolor: alpha(COLORS.primary || '#2563eb', 0.08),
                                                            '&:hover': { bgcolor: alpha(COLORS.primary || '#2563eb', 0.2) }
                                                        }}
                                                        onClick={(e) => { e.stopPropagation(); handleRowClick(fact); }}
                                                    >
                                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Imprimer la facture (PDF)">
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            color: '#475569',
                                                            bgcolor: '#f1f5f9',
                                                            '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' }
                                                        }}
                                                        onClick={(e) => { e.stopPropagation(); handlePrintClick(fact); }}
                                                    >
                                                        <PrintIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Saisir un encaissement pour ce client">
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            color: '#16a34a',
                                                            bgcolor: '#dcfce7',
                                                            '&:hover': { bgcolor: '#bbf7d0', color: '#15803d' }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const cl = fact.toClient || fact.client;
                                                            navigate('/facturation/encaissements', { state: { client: cl, factureId: fact.id } });
                                                        }}
                                                    >
                                                        <PaymentIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Extrait / Compte client">
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            color: '#7c3aed',
                                                            bgcolor: '#ede9fe',
                                                            '&:hover': { bgcolor: '#ddd6fe', color: '#6d28d9' }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const cl = fact.toClient || fact.client;
                                                            navigate('/facturation/compte-client', { state: { client: cl } });
                                                        }}
                                                    >
                                                        <MenuBookIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Supprimer la facture">
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            color: '#dc2626',
                                                            bgcolor: '#fee2e2',
                                                            '&:hover': { bgcolor: '#fecaca', color: '#b91c1c' }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteFacture(fact);
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* KEYBOARD SHORTCUTS & NAVIGATION HELPER BAR */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mt: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.2,
                        px: 2,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        bgcolor: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981' }} />
                        Raccourcis clavier :
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="caption" sx={{ color: '#334155', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <kbd style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 6px', fontSize: '0.75rem', fontWeight: 700 }}>↑</kbd>
                            <kbd style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 6px', fontSize: '0.75rem', fontWeight: 700 }}>↓</kbd>
                            Naviguer
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#334155', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <kbd style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 6px', fontSize: '0.75rem', fontWeight: 700 }}>↵ Entrée</kbd>
                            Ouvrir le détail
                        </Typography>
                    </Stack>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: 1.2,
                        px: 2,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        bgcolor: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <SwapHorizIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Défilement horizontal disponible si l'écran est réduit (Shift + Molette ou barre de défilement)
                    </Typography>
                </Paper>
            </Stack>
        </Box>
    );
};

export default GestionClientPage;
